import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { EditFrameEntry, SiteFrameEntry } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import React, { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { selectFrame } from 'redux/db/reducers';
import { setInspectorLeftPanelWidth, showContextMenu } from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { getElementName, frameSiteUrl } from 'utils';
import ResizablePanel from '../ResizablePanel';
import EditInput from './EditInput';
import * as S from './styles';

interface InspectorLeftPanelProps {
	frameId: string;
	prototypeWidth?: number;
}

const InspectorLeftPanel = ({ frameId, prototypeWidth = undefined }: InspectorLeftPanelProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const scrollingAreaRef = useRef<HTMLDivElement>(null);

	const { frame, inspectorLeftPanelWidth } = useSelector((r: RootReducer) => {
		return {
			frame: selectFrame(r.db, frameId),
			inspectorLeftPanelWidth: r.mode.inspectorLeftPanelWidth,
		};
	}, shallowEqual);

	const siteUrl = frame ? frameSiteUrl(frame) : null;
	const isImageFrame = !siteUrl;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onSiteClick = (siteId: string) => {
		if (frame?.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		// no reason to await here, so not gonna await
		forge.updateFrame(
			{
				update: {
					id: frameId,
					siteId,
				},
			},
			true
		);
	};

	const onContextMenuInPrompt = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	//------------------------------------------------------------------------------------------------
	// AUTO-SCROLLING
	//------------------------------------------------------------------------------------------------
	// Auto-scroll to bottom when new entries are added
	useEffect(() => {
		if (scrollingAreaRef.current && frame?.entries) {
			// Use setTimeout to ensure the DOM has updated with the new entries
			setTimeout(() => {
				if (scrollingAreaRef.current) {
					scrollingAreaRef.current.style.scrollBehavior = 'smooth';
					scrollingAreaRef.current.scrollTop = scrollingAreaRef.current.scrollHeight;
				}
			}, 100);
		}
	}, [frame?.entries?.length]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	if (!frame) return null;

	const renderSiteFrameEntry = (entry: SiteFrameEntry) => {
		return (
			<S.SiteEntry
				key={entry.id}
				active={frame.siteId !== entry.siteId}
				onClick={() => onSiteClick(entry.siteId)}
				disabled={false}
			>
				<S.SiteLabelHeader>
					<S.SiteLabel>{entry.label}</S.SiteLabel>
					<S.SiteSpacer />
					<S.SiteIcon>
						<Icon
							type={frame.siteId === entry.siteId ? IconType.CHECK : IconType.LINE_ARROW_RIGHT}
							width={16}
							height={16}
						/>
					</S.SiteIcon>
				</S.SiteLabelHeader>
				<S.SiteLabelDetails>
					<S.SiteSubLabel>{entry.subLabel}</S.SiteSubLabel>
				</S.SiteLabelDetails>
			</S.SiteEntry>
		);
	};

	const renderEditFrameEntry = (entry: EditFrameEntry) => {
		return (
			<React.Fragment key={entry.id}>
				<S.EditEntry>
					{entry.selectedDomElements && entry.selectedDomElements.length > 0 && (
						<S.SelectedElementsContainer>
							{entry.selectedDomElements.map((element) => (
								<S.SelectedElementEntry key={element.id}>
									{getElementName(element)}
								</S.SelectedElementEntry>
							))}
						</S.SelectedElementsContainer>
					)}
					<S.Prompt onContextMenu={onContextMenuInPrompt}>{entry.prompt}</S.Prompt>
				</S.EditEntry>
			</React.Fragment>
		);
	};

	const renderEntries = () => {
		// legacy frames do not have a site entry. let's spoof one.
		// note that adding a single edit should create a site entry. so we don't need to handle the case
		// where we want to show frame entries despite there not being a site entry.
		if (
			!frame.entries ||
			frame.entries.length === 0 ||
			frame.entries.find((e) => e.kind === 'site') === undefined
		) {
			return (
				<S.Entries>
					{renderSiteFrameEntry({
						kind: 'site',
						id: 'legacy',
						createdAt: new Date(),
						deletedAt: null,
						siteId: frame.siteId || '',
						label: 'Version 1',
					})}
				</S.Entries>
			);
		}

		// Sort entries by createdAt
		const sortedEntries = [...frame.entries].sort(
			(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
		);

		return (
			<S.Entries>
				{sortedEntries.map((entry) => {
					if (entry.kind === 'site') {
						return renderSiteFrameEntry(entry);
					} else if (entry.kind === 'edit') {
						return renderEditFrameEntry(entry);
					}
					return null; // Skip comment entries
				})}
			</S.Entries>
		);
	};

	const renderEmptyState = () => {
		return (
			<S.EmptyState>
				<S.EmptyStateIcon>
					<Icon type={IconType.FRAME} width={48} height={48} color={theme.gray[400]} />
				</S.EmptyStateIcon>
				<S.EmptyStateText>No editing available</S.EmptyStateText>
				<S.EmptyStateSubtext>
					This panel is for editing prototype frames. Imported images don't support version history or editing.
				</S.EmptyStateSubtext>
			</S.EmptyState>
		);
	};

	return (
		<S.PanelWrapper>
			<ResizablePanel
				resizeOnSide="right"
				size={inspectorLeftPanelWidth}
				onResize={(w) => dispatch(setInspectorLeftPanelWidth(w))}
			>
				<S.ScrollingArea ref={scrollingAreaRef}>
					{isImageFrame ? renderEmptyState() : renderEntries()}
				</S.ScrollingArea>
				{!isImageFrame && <EditInput frame={frame} prototypeWidth={prototypeWidth} />}
			</ResizablePanel>
		</S.PanelWrapper>
	);
};

export default InspectorLeftPanel;
